import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { useTranslation } from "react-i18next";
import { LOGO_DARK_URL } from "../lib/brand";
import LanguageSwitcher from "./LanguageSwitcher";

export default function Navbar() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();
  const isLanding = pathname === "/";

  const links = [
    { key: "home", href: "#accueil", type: "anchor" },
    { key: "properties", href: "/biens", type: "route" },
    { key: "estimation", href: "/estimation", type: "route" },
    { key: "services", href: "#services", type: "anchor" },
    { key: "agency", href: "#agence", type: "anchor" },
    { key: "contact", href: "#contact", type: "anchor" },
  ];

  const linkTo = (l) => {
    if (l.type === "route") return l.href;
    return isLanding ? l.href : `/${l.href}`;
  };
  const testId = (l) => `nav-${l.href.replace(/^[#/]/, "") || "home"}`;

  useEffect(() => {
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        setScrolled(window.scrollY > 12);
        ticking = false;
      });
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  // Prefetch the heavy /biens route as soon as the visitor hovers (desktop)
  // or touches (mobile) the nav link, so the catalogue page opens instantly.
  const prefetchBiens = () => {
    import("../pages/AllProperties").catch(() => {});
  };
  const prefetchEstimation = () => {
    import("../pages/Estimation").catch(() => {});
  };
  const onLinkHover = (l) => {
    if (l.href === "/biens") prefetchBiens();
    else if (l.href === "/estimation") prefetchEstimation();
  };

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-[#F9F8F6]/95 backdrop-blur border-b border-[#E5E0D8]" : "bg-transparent"
      }`}
    >
      {/* Dark gradient strip behind the bar when transparent — guarantees readable
          crisp text over ANY hero photo (bright skies, light walls, etc.). The
          gradient is pointer-events-none so it never blocks clicks. */}
      {!scrolled && (
        <div
          aria-hidden="true"
          className="absolute inset-x-0 top-0 h-[140%] pointer-events-none"
          style={{
            background:
              "linear-gradient(180deg, rgba(0,0,0,0.62) 0%, rgba(0,0,0,0.42) 45%, rgba(0,0,0,0.18) 80%, rgba(0,0,0,0) 100%)",
          }}
        />
      )}
      <div className="relative mx-auto max-w-[1440px] px-4 sm:px-6 md:px-10 lg:px-10 xl:px-16 py-5 flex items-center justify-between gap-4">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-3 flex-shrink-0">
          <img
            src={LOGO_DARK_URL}
            alt="IMMO SOOO Service"
            className={`w-auto transition-all duration-500 ${
              scrolled
                ? "h-11 md:h-12 xl:h-16"
                : "h-10 md:h-12 xl:h-14 drop-shadow-[0_2px_10px_rgba(0,0,0,0.55)]"
            }`}
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-5 xl:gap-9 min-w-0">
          {links.map((l) => (
            <a
              key={l.href}
              href={linkTo(l)}
              data-testid={testId(l)}
              onMouseEnter={() => onLinkHover(l)}
              onFocus={() => onLinkHover(l)}
              onTouchStart={() => onLinkHover(l)}
              className={`text-[12px] xl:text-[13px] tracking-[0.10em] xl:tracking-[0.12em] uppercase whitespace-nowrap transition-colors ${
                scrolled
                  ? "text-[#1A1A1A] hover:text-[#A3805B]"
                  : "text-white hover:text-[#D4B58C] drop-shadow-[0_1px_2px_rgba(0,0,0,0.7)]"
              }`}
            >
              {t(`nav.${l.key}`)}
            </a>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3 flex-shrink-0">
          <LanguageSwitcher />
          <a
            href="tel:+352691303091"
            className={`hidden xl:flex items-center gap-2 text-[12px] tracking-[0.12em] uppercase whitespace-nowrap transition-colors ${
              scrolled
                ? "text-[#1A1A1A] hover:text-[#A3805B]"
                : "text-white hover:text-[#D4B58C] drop-shadow-[0_1px_2px_rgba(0,0,0,0.7)]"
            }`}
            data-testid="nav-phone"
          >
            <Phone size={14} strokeWidth={1.5} />
            +352 691 30 30 91
          </a>
          <Link
            to="/estimation"
            onMouseEnter={prefetchEstimation}
            onFocus={prefetchEstimation}
            className="btn-primary btn-gold whitespace-nowrap text-[11px] xl:text-[12px] px-4 py-3 xl:px-6 xl:py-4"
            data-testid="nav-estimation-btn"
          >
            {t("nav.estimation")}
          </Link>
        </div>

        <button
          className="lg:hidden p-2"
          onClick={() => setOpen(!open)}
          aria-label="menu"
          data-testid="nav-mobile-toggle"
        >
          {open ? <X /> : <Menu />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-[#F9F8F6] border-t border-[#E5E0D8]" data-testid="mobile-menu">
          <div className="px-6 py-4 flex flex-col gap-4">
            {links.map((l) => (
              <a
                key={l.href}
                href={linkTo(l)}
                onClick={() => setOpen(false)}
                onTouchStart={() => onLinkHover(l)}
                className="text-sm tracking-[0.12em] uppercase"
              >
                {t(`nav.${l.key}`)}
              </a>
            ))}
            <div className="flex items-center justify-between pt-2 border-t border-[#E5E0D8]">
              <LanguageSwitcher variant="mobile" />
              <Link to="/estimation" onClick={() => setOpen(false)} className="btn-primary btn-gold">
                {t("nav.estimation")}
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
